import type {
  Announcement,
  AnnouncementChannels,
  AnnouncementKind,
  Audience,
  AudienceType,
  CreateAnnouncementRequest,
  PortalRole,
  UpdateAnnouncementRequest,
} from '@central/api';
import type { StatusTone } from '@central/ui';

export const allRoles: PortalRole[] = ['Owner', 'Member', 'Viewer'];

export const roleLabel: Record<PortalRole, string> = {
  Owner: 'Owners',
  Member: 'Members',
  Viewer: 'Viewers',
};

export const kindChip: Record<AnnouncementKind, { tone: StatusTone; label: string }> = {
  release: { tone: 'blu', label: 'Release' },
  maintenance: { tone: 'warn', label: 'Maintenance' },
  notice: { tone: 'unk', label: 'Notice' },
  security: { tone: 'warn', label: 'Security' },
};

export const kindOptions: Array<{ value: AnnouncementKind; label: string }> = [
  { value: 'release', label: 'New release' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'notice', label: 'Notice' },
  { value: 'security', label: 'Security' },
];

/** The composer's form state. Dates are input values, not wire times. */
export interface Draft {
  kind: AnnouncementKind;
  releaseVersion: string;
  title: string;
  body: string;
  audienceType: AudienceType;
  customerIds: string[];
  versionBelow: string;
  maintenanceCovers: string;
  /** Never empty; all three = every role. */
  roles: PortalRole[];
  channels: AnnouncementChannels;
  publishMode: 'now' | 'later';
  /** datetime-local value, read as UTC. */
  publishAt: string;
  /** Last day it shows, yyyy-mm-dd (UTC); empty = no end. */
  endsOn: string;
}

/** Error text keyed by the server's field names. */
export type DraftErrors = Partial<
  Record<
    | 'title'
    | 'body'
    | 'releaseVersion'
    | 'audience.customerIds'
    | 'audience.versionBelow'
    | 'audience.maintenanceCovers'
    | 'channels'
    | 'publishAt'
    | 'endsAt',
    string
  >
>;

export function emptyDraft(): Draft {
  return {
    kind: 'notice',
    releaseVersion: '',
    title: '',
    body: '',
    audienceType: 'all',
    customerIds: [],
    versionBelow: '',
    maintenanceCovers: '',
    roles: [...allRoles],
    channels: { popup: true, bell: true, email: false, banner: false },
    publishMode: 'now',
    publishAt: '',
    endsOn: '',
  };
}

export function draftFrom(a: Announcement): Draft {
  return {
    kind: a.kind,
    releaseVersion: a.releaseVersion ?? '',
    title: a.title,
    body: a.body,
    audienceType: a.audience.type,
    customerIds: a.audience.customerIds,
    versionBelow: a.audience.versionBelow ?? '',
    maintenanceCovers: a.audience.maintenanceCovers ?? '',
    roles: a.audience.roles.length ? a.audience.roles : [...allRoles],
    channels: a.channels,
    publishMode: 'now',
    publishAt: '',
    endsOn: dayFromEndsAt(a.endsAt),
  };
}

/** The audience on the wire: only the key its type uses; roles empty when every role. */
export function buildAudience(d: Draft): Audience {
  const a: Audience = {
    type: d.audienceType,
    customerIds: d.audienceType === 'customers' ? d.customerIds : [],
    roles: d.roles.length === allRoles.length ? [] : allRoles.filter((r) => d.roles.includes(r)),
  };
  if (d.audienceType === 'version_below') a.versionBelow = d.versionBelow.trim();
  if (d.audienceType === 'maintenance_covers') a.maintenanceCovers = d.maintenanceCovers.trim();
  return a;
}

/** Body for POST /announcements. An empty releaseVersion clears it. */
export function buildRequest(d: Draft): CreateAnnouncementRequest {
  return {
    kind: d.kind,
    title: d.title.trim(),
    body: d.body,
    releaseVersion: d.kind === 'release' ? d.releaseVersion : '',
    audience: buildAudience(d),
    channels: d.channels,
    endsAt: d.endsOn ? endsAtFromDay(d.endsOn) : undefined,
  };
}

/** Body for PATCH /announcements on a draft: an empty end date clears the stored one. */
export function buildPatch(d: Draft): UpdateAnnouncementRequest {
  return { ...buildRequest(d), endsAt: d.endsOn ? endsAtFromDay(d.endsOn) : null };
}

/** "Stop showing after Nov 30" = endsAt at the start of Dec 1, UTC. */
export function endsAtFromDay(day: string): string {
  const t = new Date(`${day}T00:00:00Z`).getTime() + 86_400_000;
  return new Date(t).toISOString().replace('.000Z', 'Z');
}

/** The last day an announcement ending at endsAt shows, yyyy-mm-dd. */
export function dayFromEndsAt(endsAt: string | undefined): string {
  if (!endsAt) return '';
  return new Date(new Date(endsAt).getTime() - 1).toISOString().slice(0, 10);
}

/** RFC 3339 for a datetime-local value read as UTC. */
export function publishAtFrom(local: string): string {
  return `${local}:00Z`;
}

const versionRe = /^\d+\.\d+(\.\d+){0,2}$/;
const lineRe = /^\d+\.\d+$/;

/** Checks before a save ('save') or a publish ('publish'). The server checks again. */
export function validate(d: Draft, action: 'save' | 'publish', now: Date): DraftErrors {
  const e: DraftErrors = {};
  const title = d.title.trim();
  if (!title) e.title = 'Enter a title.';
  else if ([...title].length > 200) e.title = 'At most 200 characters.';
  if ([...d.body].length > 20000) e.body = 'At most 20,000 characters.';
  if (d.audienceType === 'customers' && d.customerIds.length === 0)
    e['audience.customerIds'] = 'Choose at least one customer.';
  if (d.audienceType === 'version_below' && !versionRe.test(d.versionBelow.trim()))
    e['audience.versionBelow'] = 'A version such as 1.0 or 1.0.3.';
  if (d.audienceType === 'maintenance_covers' && !lineRe.test(d.maintenanceCovers.trim()))
    e['audience.maintenanceCovers'] = 'A release line (MAJOR.MINOR) such as 1.1.';
  if (action === 'save') return e;

  if (!d.body.trim()) e.body = 'Write the message people will read.';
  if (!Object.values(d.channels).some(Boolean))
    e.channels = 'Choose at least one place for it to show.';
  let start = now.getTime();
  if (d.publishMode === 'later') {
    if (!d.publishAt) e.publishAt = 'Choose when to publish.';
    else {
      start = new Date(publishAtFrom(d.publishAt)).getTime();
      if (!(start > now.getTime())) e.publishAt = 'Choose a time in the future.';
    }
  }
  if (d.endsOn && !e.publishAt && new Date(endsAtFromDay(d.endsOn)).getTime() <= start)
    e.endsAt = 'Stop showing it after it is published.';
  return e;
}

const known = new Set<string>([
  'title',
  'body',
  'releaseVersion',
  'audience.customerIds',
  'audience.versionBelow',
  'audience.maintenanceCovers',
  'channels',
  'publishAt',
  'endsAt',
]);

/** The server's 400 fields that the form shows next to an input. */
export function fieldErrors(fields: Record<string, string> | undefined): DraftErrors {
  const out: DraftErrors = {};
  for (const [k, v] of Object.entries(fields ?? {}))
    if (known.has(k)) out[k as keyof DraftErrors] = v;
  return out;
}

/** "Maintenance covers 1.1 · Owners, Members". */
export function describeAudience(a: Audience): string {
  let s: string;
  switch (a.type) {
    case 'all':
      s = 'All customers';
      break;
    case 'customers':
      s = `${a.customerIds.length} selected customer${a.customerIds.length === 1 ? '' : 's'}`;
      break;
    case 'version_below':
      s = `Customers before ${a.versionBelow ?? ''}`;
      break;
    case 'maintenance_covers':
      s = `Maintenance covers ${a.maintenanceCovers ?? ''}`;
      break;
  }
  if (a.roles.length && a.roles.length < allRoles.length)
    s +=
      ' · ' +
      allRoles
        .filter((r) => a.roles.includes(r))
        .map((r) => roleLabel[r])
        .join(', ');
  return s;
}

/** "people", or "owners" when only Owners are reached. */
export function peopleWord(a: Audience): string {
  return a.roles.length === 1 && a.roles[0] === 'Owner' ? 'owners' : 'people';
}

/** "1.1.0" and "1.1.0.1" -> "1.1". */
export function releaseLine(version: string): string {
  return version.split('.').slice(0, 2).join('.');
}
